import { createContext, use, useMemo, type ReactNode } from 'react'

import type { RegisteredContribution, Registry } from './registry'
import type { AdminHomeLayout, LinkAccess, SlotContext, SlotKind } from './types'

const RegistryContext = createContext<Registry | null>(null)

/** Supplies the registry `App` builds from `src/app/features.ts`. */
export function RegistryProvider(props: {
  readonly registry: Registry
  readonly children: ReactNode
}) {
  return <RegistryContext value={props.registry}>{props.children}</RegistryContext>
}

/** The registry. Throws outside {@link RegistryProvider}. */
export function useRegistry(): Registry {
  const registry = use(RegistryContext)
  if (registry === null) {
    throw new Error('useRegistry must be used inside a RegistryProvider.')
  }
  return registry
}

/** Wraps `children` in every feature's app-wide provider, the first registered outermost. */
export function FeatureProviders(props: { readonly children: ReactNode }) {
  const { providers } = useRegistry()
  return providers.reduceRight<ReactNode>(
    (children, Provider) => <Provider>{children}</Provider>,
    props.children,
  )
}

/**
 * What other features registered in a host page's slot, in registry order. The host renders
 * them and imports nothing from the contributors. A tab or link that names a permission is left out
 * for a viewer who does not hold it, on the host's record when `context` names one.
 */
export function useSlot<K extends SlotKind>(
  slot: string,
  kind: K,
  context?: SlotContext,
): readonly Extract<RegisteredContribution, { readonly kind: K }>[] {
  const { slots } = useRegistry()
  const canSee = useLinkAccess()
  return useMemo(
    () =>
      (slots.get(slot) ?? []).filter(
        (contribution): contribution is Extract<RegisteredContribution, { readonly kind: K }> =>
          contribution.kind === kind && ((contribution.kind !== 'tab' && contribution.kind !== 'link') || canSee(contribution, context)),
      ),
    [slots, slot, kind, canSee, context],
  )
}

const everyone: LinkAccess = () => true

const LinkAccessContext = createContext<LinkAccess>(everyone)

/**
 * Whether what the viewer may see is known: `loading` and `error` pass nothing guarded, and say
 * nothing about what the viewer holds.
 */
export type LinkAccessStatus = 'loading' | 'ready' | 'error'

const LinkAccessStatusContext = createContext<LinkAccessStatus>('ready')

/**
 * Says which nav items, Admin Home links and contributed tabs the viewer may see. Access Control
 * renders it from its app-wide provider (AC-18); without it everyone sees everything. A provider
 * that narrows an outer one's answer leaves `status` out and keeps the outer one's.
 */
export function LinkAccessProvider(props: {
  readonly value: LinkAccess
  readonly status?: LinkAccessStatus
  readonly children: ReactNode
}) {
  const outerStatus = use(LinkAccessStatusContext)
  return (
    <LinkAccessContext value={props.value}>
      <LinkAccessStatusContext value={props.status ?? outerStatus}>{props.children}</LinkAccessStatusContext>
    </LinkAccessContext>
  )
}

/** Whether the viewer may see a nav item, Admin Home link, or contributed tab or link. */
export function useLinkAccess(): LinkAccess {
  return use(LinkAccessContext)
}

/** Whether what the viewer may see is known, so a page may act on a link they lack. */
export function useLinkAccessStatus(): LinkAccessStatus {
  return use(LinkAccessStatusContext)
}

const registryOrder: AdminHomeLayout = {
  status: 'ready',
  mostUsed: [],
  sectionOrder: [],
  recordClick: () => undefined,
  retry: () => undefined,
}

const AdminHomeLayoutContext = createContext<AdminHomeLayout>(registryOrder)

/**
 * Gives Admin Home the viewer's layout. The feature that fills Admin Home's hook (SM-29) renders
 * it from its `adminHome.layout` wrapper; until then the page keeps registry order.
 */
export function AdminHomeLayoutProvider(props: {
  readonly value: AdminHomeLayout
  readonly children: ReactNode
}) {
  return <AdminHomeLayoutContext value={props.value}>{props.children}</AdminHomeLayoutContext>
}

/** The viewer's Admin Home layout. */
export function useAdminHomeLayout(): AdminHomeLayout {
  return use(AdminHomeLayoutContext)
}
